import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Typography, Space, Divider, List, Tag, Spin, Avatar, Dropdown } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  BookOutlined, 
  NotificationOutlined, 
  FileTextOutlined, 
  UserOutlined,
  LoginOutlined,
  CalendarOutlined,
  LogoutOutlined,
  MailOutlined,
  TeamOutlined,
  CrownOutlined,
  EyeOutlined
} from '@ant-design/icons';
import LanguageSwitcher from './common/LanguageSwitcher';
import { lawService } from '../services/lawService';
import { announcementService } from '../services/announcementService';
import { legislativeNoticeService } from '../services/legislativeNoticeService';
import { publicationService } from '../services/publicationService';
import { Law, Announcement, LegislativeNotice, Publication, LawCategory, LawStatus } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { formatLocalizedDate } from '../utils/dateUtils';

const { Title, Paragraph, Text } = Typography;

const Home: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [recentData, setRecentData] = useState({
    laws: [] as Law[],
    announcements: [] as Announcement[],
    legislativeNotices: [] as LegislativeNotice[],
    publications: [] as Publication[]
  });

  useEffect(() => {
    const fetchRecentData = async () => {
      try {
        setLoading(true);
        
        // 권한에 따라 다른 API 호출
        let laws, announcements, legislativeNotices, publications;
        
        if (isAdmin) {
          // 관리자는 모든 게시물을 볼 수 있음
          [announcements, legislativeNotices, publications] = await Promise.all([
            announcementService.getRecentAnnouncements(3).then(data => Array.isArray(data) ? data : []),
            legislativeNoticeService.getRecentLegislativeNotices(3).then(data => Array.isArray(data) ? data : []),
            publicationService.getRecentPublications(3).then(data => Array.isArray(data) ? data : [])
          ]);
          
          // 관리자의 규범류: 모든 규범류를 가져와서 최근 등록된 순으로 3개 선택
          const allLawsData = await lawService.searchLaws({
            limit: 50,
            sortBy: 'category_priority',
            sortOrder: 'ASC',
          });
          // 교헌 먼저, 나머지는 제목 오름차순으로 정렬 후 최근 등록된 순으로 3개 선택
          const kyohun = allLawsData.laws.filter(law => law.category === '교헌');
          const others = allLawsData.laws.filter(law => law.category !== '교헌').sort((a, b) => a.title.localeCompare(b.title, 'ko'));
          const sortedLaws = [...kyohun, ...others];
          // 최근 등록된 순으로 정렬 (created_at 기준)
          const recentLaws = sortedLaws
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
            .slice(0, 3);
          laws = recentLaws;
        } else {
          // 일반 사용자는 승인된 게시물만 볼 수 있음
          [announcements, legislativeNotices, publications] = await Promise.all([
            announcementService.getAllAnnouncements().then(data => 
              Array.isArray(data) ? data.filter(item => item.is_approved).slice(0, 3) : []
            ),
            legislativeNoticeService.getAllLegislativeNotices().then(data => 
              Array.isArray(data) ? data.filter(item => item.is_approved).slice(0, 3) : []
            ),
            publicationService.getAllPublications().then(data => 
              Array.isArray(data) ? data.filter(item => item.is_approved).slice(0, 3) : []
            )
          ]);
          
          // 일반 사용자의 규범류: 승인된 규범류와 미승인 규범류의 마지막 승인된 버전을 가져와서 최근 등록된 순으로 3개 선택
          const allLawsData = await lawService.getLawsForGeneralUser();
          const recentLaws = allLawsData
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
            .slice(0, 3);
          laws = recentLaws;
        }

        setRecentData({
          laws,
          announcements,
          legislativeNotices,
          publications
        });
      } catch (error) {
        console.error('최근 데이터 로드 실패:', error);
        console.error('에러 상세 정보:', {
          message: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
          response: (error as any)?.response?.data
        });
      } finally {
        setLoading(false);
      }
    };

    fetchRecentData();
  }, [isAdmin]);

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  const handleLogin = () => {
    navigate(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const getCategoryColor = (category: LawCategory) => {
    switch (category) {
      case LawCategory.KYOHUN:
        return 'blue';
      case LawCategory.KYOGYU:
        return 'green';
      case LawCategory.KYORYEONG:
        return 'orange';
      case LawCategory.YEGYU:
        return 'purple';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: LawStatus) => {
    switch (status) {
      case LawStatus.ENACTED:
        return 'green';
      case LawStatus.AMENDED:
        return 'blue';
      case LawStatus.FULLY_AMENDED:
        return 'purple';
      case LawStatus.ABOLISHED:
        return 'red';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: LawStatus) => {
    switch (status) {
      case LawStatus.ENACTED:
        return t('laws.enacted');
      case LawStatus.AMENDED:
        return t('laws.amended');
      case LawStatus.FULLY_AMENDED:
        return t('laws.fullyAmended');
      case LawStatus.ABOLISHED:
        return t('laws.abolished');
      default:
        return status;
    }
  };

  const getCategoryText = (category: LawCategory) => {
    switch (category) {
      case LawCategory.KYOHUN: return t('laws.kyohun');
      case LawCategory.KYOGYU: return t('laws.kyogyu');
      case LawCategory.KYORYEONG: return t('laws.kyoryeong');
      case LawCategory.YEGYU: return t('laws.yegyu');
      default: return category;
    }
  };

  // 사용자 메뉴 아이템
  const userMenuItems = [
    {
      key: 'profile',
      label: t('navigation.profile'),
      icon: <UserOutlined />,
      onClick: () => navigate('/profile')
    },
    {
      key: 'logout',
      label: t('auth.logout'),
      icon: <LogoutOutlined />,
      onClick: handleLogout
    }
  ];

  const menuItems = [
    {
      title: t('laws.title'),
      description: t('laws.description'),
      icon: <BookOutlined style={{ fontSize: '24px' }} />,
      path: '/laws',
      color: '#1890ff',
      data: recentData.laws,
      type: 'laws'
    },
    {
      title: t('publications.title'),
      description: t('publications.description'),
      icon: <FileTextOutlined style={{ fontSize: '24px' }} />,
      path: '/publications',
      color: '#722ed1',
      data: recentData.publications,
      type: 'publications'
    },
    {
      title: t('legislativeNotices.title'),
      description: t('legislativeNotices.description'),
      icon: <FileTextOutlined style={{ fontSize: '24px' }} />,
      path: '/legislative-notices',
      color: '#fa8c16',
      data: recentData.legislativeNotices,
      type: 'legislativeNotices'
    },
    {
      title: t('announcements.title'),
      description: t('announcements.description'),
      icon: <NotificationOutlined style={{ fontSize: '24px' }} />,
      path: '/announcements',
      color: '#52c41a',
      data: recentData.announcements,
      type: 'announcements'
    }
  ];

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto', position: 'relative' }}>
      {/* 오른쪽 상단 사용자 정보, 언어 선택기, 로그인/회원가입 버튼 */}
      <div style={{ 
        position: 'absolute', 
        top: '16px', 
        right: '16px', 
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
        <LanguageSwitcher />
        {isAuthenticated ? (
          <Dropdown
            menu={{ items: userMenuItems }}
            placement="bottomRight"
            trigger={['click']}
          >
            <Button 
              type="text" 
              icon={<UserOutlined />}
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                height: '32px',
                padding: '0 8px'
              }}
            >
              <Avatar 
                size={24} 
                icon={<UserOutlined />} 
                style={{ backgroundColor: '#1890ff' }}
              />
              <Text strong style={{ fontSize: '14px' }}>
                {user?.username}
              </Text>
              {isAdmin && (
                <Tag color="red" style={{ fontSize: '10px', margin: 0 }}>
                  {t('navigation.admin')}
                </Tag>
              )}
            </Button>
          </Dropdown>
        ) : (
          <Space>
            <Button 
              type="primary" 
              size="small"
              icon={<LoginOutlined />}
              onClick={handleLogin}
            >
              {t('home.login')}
            </Button>
            <Button 
              size="small"
              icon={<UserOutlined />}
              onClick={() => handleNavigate('/register')}
            >
              {t('home.register')}
            </Button>
          </Space>
        )}
      </div>
      
      {/* 헤더 섹션 */}
      <div style={{ textAlign: 'center', marginBottom: '48px', paddingTop: '80px' }}>
        <Title level={1} style={{ marginBottom: '16px' }}>
          {t('home.title')}
        </Title>
        <Paragraph style={{ fontSize: '16px', color: '#666', marginBottom: '24px' }}>
          {t('home.subtitle')}
        </Paragraph>
        
        {/* 관리자 전용 기능 - 로그인된 사용자만 */}
        {isAuthenticated && isAdmin && (
          <div style={{ marginTop: '24px', width: '100%', maxWidth: '600px', margin: '0 auto' }}>
            <Divider style={{ margin: '16px 0' }}>
              <Title level={4} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <CrownOutlined style={{ color: '#ff4d4f' }} />
                {t('home.adminSection.title')}
              </Title>
            </Divider>
            <Row gutter={[16, 16]} justify="center">
              <Col xs={24} sm={12}>
                <Card
                  hoverable
                  size="small"
                  style={{ 
                    cursor: 'pointer',
                    border: '2px solid #ff4d4f20',
                    transition: 'all 0.3s'
                  }}
                  onClick={() => handleNavigate('/admin/users')}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <TeamOutlined style={{ fontSize: '20px', color: '#ff4d4f' }} />
                    <div style={{ flex: 1 }}>
                      <Text strong>{t('home.adminSection.userManagement')}</Text>
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                        {t('home.adminSection.userManagementDesc')}
                      </div>
                    </div>
                    <Button type="primary" danger size="small">
                      {t('home.adminSection.goTo')}
                    </Button>
                  </div>
                </Card>
              </Col>
              <Col xs={24} sm={12}>
                <Card
                  hoverable
                  size="small"
                  style={{ 
                    cursor: 'pointer',
                    border: '2px solid #ff4d4f20',
                    transition: 'all 0.3s'
                  }}
                  onClick={() => handleNavigate('/admin/email')}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <MailOutlined style={{ fontSize: '20px', color: '#ff4d4f' }} />
                    <div style={{ flex: 1 }}>
                      <Text strong>{t('home.adminSection.emailSender')}</Text>
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                        {t('home.adminSection.emailSenderDesc')}
                      </div>
                    </div>
                    <Button type="primary" danger size="small">
                      {t('home.adminSection.goTo')}
                    </Button>
                  </div>
                </Card>
              </Col>
            </Row>
          </div>
        )}
      </div>

      <Divider />

      {/* 통합된 게시판 카드들 */}
      <Row gutter={[24, 24]} style={{ alignItems: 'stretch' }}>
        {menuItems.map((item, index) => (
          <Col xs={24} sm={12} lg={6} key={index}>
            <Card
              hoverable
              style={{ 
                cursor: 'pointer',
                border: `2px solid ${item.color}20`,
                transition: 'all 0.3s',
                height: '100%'
              }}
              onClick={() => handleNavigate(item.path)}
              styles={{
                body: {
                  padding: '20px',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column'
                }
              }}
            >
              {/* 메뉴 헤더 섹션 */}
              <div style={{ 
                textAlign: 'center', 
                marginBottom: '16px',
                paddingBottom: '16px',
                borderBottom: `1px solid ${item.color}20`
              }}>
                <div style={{ 
                  color: item.color, 
                  marginBottom: '12px',
                  display: 'flex',
                  justifyContent: 'center'
                }}>
                  {item.icon}
                </div>
                <Title level={4} style={{ marginBottom: '8px', fontSize: '16px' }}>
                  {item.title}
                </Title>
                <Paragraph style={{ 
                  color: '#666', 
                  margin: 0,
                  lineHeight: '1.4',
                  fontSize: '13px'
                }}>
                  {item.description}
                </Paragraph>
              </div>

              {/* 최신글 섹션 */}
              <div style={{ flex: 1, marginBottom: '16px' }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  marginBottom: '12px'
                }}>
                  <Text strong style={{ fontSize: '14px', color: item.color }}>
                    최신글
                  </Text>
                  <Button 
                    type="link" 
                    size="small"
                    style={{ padding: 0, height: 'auto', fontSize: '12px' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNavigate(item.path);
                    }}
                  >
                    더보기
                  </Button>
                </div>
                
                {item.data.length > 0 ? (
                  <List
                    size="small"
                    dataSource={item.data as any[]}
                    renderItem={(post: any) => (
                      <List.Item
                        style={{ 
                          padding: '8px 0',
                          cursor: 'pointer',
                          borderBottom: '1px solid #f0f0f0'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          let idField;
                          switch (item.type) {
                            case 'laws':
                              idField = 'law_id';
                              break;
                            case 'announcements':
                              idField = 'announcement_id';
                              break;
                            case 'legislativeNotices':
                              idField = 'legislative_notice_id';
                              break;
                            case 'publications':
                              idField = 'publication_id';
                              break;
                            default:
                              idField = `${item.type.slice(0, -1)}_id`;
                          }
                          handleNavigate(`${item.path}/${post[idField]}`);
                        }}
                      >
                        <div style={{ width: '100%' }}>
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'flex-start',
                            marginBottom: '2px'
                          }}>
                            <Text 
                              strong 
                              style={{ 
                                fontSize: '12px', 
                                flex: 1, 
                                wordBreak: 'break-all', 
                                whiteSpace: 'normal', 
                                lineHeight: '1.3',
                                color: '#333'
                              }}
                            >
                              {post.title}
                            </Text>
                            {isAdmin && (
                              <Tag 
                                color={post.is_approved ? 'green' : 'orange'}
                                style={{ marginLeft: '4px', fontSize: '9px' }}
                              >
                                {post.is_approved ? '승인' : '대기'}
                              </Tag>
                            )}
                          </div>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '2px',
                            fontSize: '10px',
                            color: '#999'
                          }}>
                            <CalendarOutlined style={{ fontSize: '10px' }} />
                            <Text type="secondary" style={{ fontSize: '10px' }}>
                              {formatDate(post.created_at)}
                            </Text>
                          </div>
                        </div>
                      </List.Item>
                    )}
                  />
                ) : (
                  <div style={{ 
                    textAlign: 'center', 
                    color: '#999', 
                    padding: '16px 0',
                    fontSize: '12px'
                  }}>
                    게시물이 없습니다
                  </div>
                )}
              </div>

              {/* 하단 버튼 */}
              <div style={{ marginTop: 'auto' }}>
                <Button 
                  type="primary" 
                  style={{ 
                    backgroundColor: item.color, 
                    borderColor: item.color,
                    width: '100%',
                    fontSize: '13px',
                    height: '32px'
                  }}
                >
                  {t('home.goTo')}
                </Button>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* 추가 정보 섹션 */}
      <div style={{ marginTop: '48px', textAlign: 'center' }}>
        <Divider />
        <Title level={3} style={{ marginBottom: '16px' }}>
          {t('home.features.title')}
        </Title>
        <Row gutter={[24, 24]}>
          <Col xs={24} sm={8}>
            <Card>
              <Title level={4}>{t('home.features.management.title')}</Title>
              <Paragraph>
                {t('home.features.management.description')}
              </Paragraph>
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <Title level={4}>{t('home.features.search.title')}</Title>
              <Paragraph>
                {t('home.features.search.description')}
              </Paragraph>
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <Title level={4}>{t('home.features.participation.title')}</Title>
              <Paragraph>
                {t('home.features.participation.description')}
              </Paragraph>
            </Card>
          </Col>
        </Row>
      </div>

      {/* 회원가입 안내 섹션 - 로그인하지 않은 사용자에게만 표시 (하단) */}
      {!isAuthenticated && (
        <div style={{ marginTop: '48px', textAlign: 'center' }}>
          <Divider />
          <Card 
            style={{ 
              maxWidth: '600px', 
              margin: '0 auto',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
              borderRadius: '10px'
            }}
            styles={{ body: { padding: '20px' } }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', color: 'white' }}>
              <MailOutlined style={{ fontSize: '22px', color: 'white' }} />
              <div style={{ flex: 1, textAlign: 'left' }}>
                <Title level={4} style={{ color: 'white', margin: 0, marginBottom: '6px', fontSize: '16px' }}>
                  {t('home.emailNotification.title')}
                </Title>
                <Paragraph style={{ color: 'white', margin: 0, fontSize: '13px', lineHeight: '1.5' }}>
                  {t('home.emailNotification.description')}
                </Paragraph>
              </div>
              <Button 
                type="primary" 
                size="middle"
                style={{ 
                  backgroundColor: 'white', 
                  color: '#667eea',
                  border: 'none',
                  fontWeight: 'bold',
                  height: '32px',
                  fontSize: '13px'
                }}
                onClick={() => handleNavigate('/register')}
              >
                {t('home.emailNotification.registerButton')}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Home; 